/**
 * Ability-Cast-VFX (BATTLE-14). Pro Ability ein eigener, sofort erkennbarer
 * Element-Effekt (docs/11 VFX Style: "kraeftig, aber niemals unlesbar"):
 *   bulwark      — Schild-Blau:     expandierender Schild-Dom + Schimmer
 *   shadowstrike — Schatten-Violett: einwaerts ziehende Schlieren + Dunkel-Puls
 *   piercingShot — Tech-Grau:       Muendungsblitz + horizontale Speed-Lines
 *   healingWave  — Heilig-Gold:     Boden-Heilkreis + aufsteigender Funkenregen
 *   plagueCloud  — Gift-Gruen:      traege Blasen-Wolke + Boden-Ring
 * Farben strikt aus KEYWORD_COLORS / ABILITY_COLORS (docs/12 §2 verbindlich);
 * Dunkel-/Hell-Toene nur als darken/lighten-Ableitung der Theme-Farbe.
 */
import type { ParticleSystem } from './particles'
import { ABILITY_COLORS } from '../ui/theme'
import { darken, rgba } from './draw3d'

/** Bollwerk: Halbkugel-Dom schliesst sich ueber der Unit, Schimmer laeuft aussen */
function bulwarkVfx(x: number, y: number, r: number, nowMs: number, ps: ParticleSystem, c: string): void {
  const groundY = y + r * 0.55
  ps.shieldDome(x, groundY, r * 1.6, c, nowMs, 680)
  ps.shimmer(x, y - r * 0.1, r * 1.15, c, nowMs, 12)
}

/** Schattenstoss: Schlieren werden ins Zentrum gesogen, kurzer Dunkel-Puls */
function shadowstrikeVfx(x: number, y: number, r: number, nowMs: number, ps: ParticleSystem, c: string): void {
  const cy = y - r * 0.1
  ps.streaksIn(x, cy, r, c, nowMs, 10)
  ps.pulse(x, cy, darken(c, 30), nowMs, r * 1.3, 320)
  ps.implosion(x, cy, c, nowMs, r * 1.25)
}

/** Durchschlag: Muendungsblitz + Speed-Lines schiessen horizontal nach aussen */
function piercingShotVfx(x: number, y: number, r: number, nowMs: number, ps: ParticleSystem, c: string): void {
  const cy = y - r * 0.3
  ps.muzzleFlash(x, cy, c, nowMs, r * 0.9)
  ps.speedLines(x, cy, c, nowMs, 10, 460)
  ps.sparks(x, cy, c, nowMs, 4, 70)
}

/** Heilwelle: weicher Boden-Heilkreis + goldener Funkenregen steigt auf */
function healingWaveVfx(x: number, y: number, r: number, nowMs: number, ps: ParticleSystem, c: string): void {
  const groundY = y + r * 0.55
  ps.groundGlow(x, groundY, c, nowMs, r * 1.55, 850)
  ps.groundRing(x, groundY, c, nowMs, r * 1.8, 750)
  ps.starRain(x, y - r * 0.15, r * 1.9, c, nowMs, 14)
}

/** Seuchenwolke: traege gruene Wolke + aufsteigende Blasen + Boden-Ring */
function plagueCloudVfx(x: number, y: number, r: number, nowMs: number, ps: ParticleSystem, c: string): void {
  ps.cloud(x, y - r * 0.1, rgba(c, 0.6), nowMs, 9, r * 1.05)
  ps.bubbles(x, y - r * 0.1, c, nowMs, 6)
  ps.groundRing(x, y + r * 0.55, c, nowMs, r * 1.9, 900)
}

export function abilityCastVfx(
  name: string,
  x: number,
  y: number,
  r: number,
  nowMs: number,
  particles: ParticleSystem,
): void {
  const color = ABILITY_COLORS[name] ?? '#ffffff'
  switch (name) {
    case 'bulwark':
      bulwarkVfx(x, y, r, nowMs, particles, color)
      break
    case 'shadowstrike':
      shadowstrikeVfx(x, y, r, nowMs, particles, color)
      break
    case 'piercingShot':
      piercingShotVfx(x, y, r, nowMs, particles, color)
      break
    case 'healingWave':
      healingWaveVfx(x, y, r, nowMs, particles, color)
      break
    case 'plagueCloud':
      plagueCloudVfx(x, y, r, nowMs, particles, color)
      break
    default:
      // unbekannte Ability: neutraler Baseline-Cast
      particles.ring(x, y, color, nowMs, r * 1.2)
      particles.sparks(x, y, color, nowMs, 5, 60)
  }
}
